/**
 * Privacy-conscious analytics interface (APP-001 §24).
 *
 * No vendor is selected in APP-001; this provides the typed interface and a
 * no-op default implementation. Only privacy-safe events (page_view,
 * sign_in_started, etc.) are emitted. Sensitive values (emails under analysis,
 * API keys, Clerk tokens, Stripe URLs, raw responses) are NEVER passed through
 * analytics.
 */

export type AnalyticsEvent =
  | { event: 'page_view'; path: string }
  | { event: 'sign_in_started' }
  | { event: 'sign_in_completed' }
  | { event: 'sign_up_completed' }
  | { event: 'dashboard_loaded' }
  | { event: 'account_sync_pending' }
  | { event: 'api_error_by_code'; code: string; status: number; type: string }
  | { event: 'theme_changed'; theme: string }
  // APP-002 onboarding + dashboard events (APP-002 §27). No sensitive payloads:
  // the `step` is a low-cardinality UI ordinal only; never account ID, Clerk ID,
  // email, plan, usage count, or request ID.
  | { event: 'onboarding_started' }
  | { event: 'onboarding_step_completed'; step: string }
  | { event: 'onboarding_completed' }
  | { event: 'onboarding_skipped' }
  | { event: 'dashboard_overview_loaded' }
  | { event: 'dashboard_action_selected'; action: string }
  | { event: 'dashboard_refresh_requested' }
  // APP-003 usage events (APP-003 §28). Privacy-safe: no usage counts, no
  // account/plan/key identifiers, no request ID, no reset timestamp. Only the
  // low-cardinality event names + safe UI ordinals (`doc_target`, `action`)
  // are emitted. The scrub guard below still rejects any leaked secret hint.
  | { event: 'usage_page_loaded' }
  | { event: 'usage_refresh_requested' }
  | { event: 'usage_documentation_opened'; doc_target: string }
  | { event: 'usage_upgrade_action_selected'; action: string }
  // APP-004 API-key events (APP-004 §34). Privacy-safe: event names only. No
  // key id, name, prefix, secret, scopes, account/Clerk id, last-used, expiry,
  // or request id is ever attached. The scrub guard rejects any leaked `eisk_`
  // hint, so a secret accidentally attached to any future payload throws.
  | { event: 'api_keys_page_loaded' }
  | { event: 'api_key_create_started' }
  | { event: 'api_key_created' }
  | { event: 'api_key_copy_requested' }
  | { event: 'api_key_renamed' }
  | { event: 'api_key_revoked' }
  | { event: 'api_key_rotation_started' }
  | { event: 'api_key_rotated' }
  | { event: 'api_keys_refresh_requested' }
  | { event: 'api_key_docs_opened' }
  // APP-005 playground events (APP-005 §42). Privacy-safe: event names + safe
  // low-cardinality ordinals only. NEVER attach the analyzed email, name, IP,
  // industry, risk score, decision, check outcomes, warning codes, request id,
  // account/Clerk id, or raw request/response. `failure_code` is an allowlisted
  // high-level error category (HTTP status family or DIRECT-011 type), never the
  // raw code if it could echo input. The scrub guard below still rejects any
  // leaked secret hint; emails are protected structurally (never attached).
  | { event: 'playground_page_loaded' }
  | { event: 'playground_sample_selected'; sample: string }
  | { event: 'playground_analysis_started' }
  | { event: 'playground_analysis_succeeded' }
  | { event: 'playground_analysis_failed_by_code'; failure_code: string }
  | { event: 'playground_result_section_opened'; section: string }
  | { event: 'playground_code_example_copied'; language: string }
  | { event: 'playground_api_keys_opened' }
  | { event: 'playground_usage_opened' }
  // APP-006 billing events. Privacy-safe: event names + safe low-cardinality
  // ordinals only. NEVER attach the Stripe customer/subscription/invoice id,
  // Checkout/Portal hosted URL, account/Clerk id, request id, payment-method
  // details, or exact billing timestamps. `plan` and `interval` are the only
  // attached values and only as low-cardinality product analytics (Pro/Ultra/
  // Growth/Mega and monthly/annual). The scrub guard below still rejects any
  // leaked Stripe host (`checkout.stripe.com` / `billing.stripe.com`) so a
  // hosted URL accidentally attached to any future payload throws.
  | { event: 'billing_page_loaded' }
  | { event: 'billing_interval_selected'; interval: string }
  | { event: 'billing_plan_selected'; plan: string }
  | { event: 'billing_checkout_started'; plan: string; interval: string }
  | { event: 'billing_checkout_redirected'; plan: string; interval: string }
  | { event: 'billing_checkout_returned'; result: string }
  // User-abandoned pending-checkout cancellation (POST
  // /billing/checkout/cancel): fires on the manual "Cancel checkout" action.
  // Event name only — never the Stripe session/customer ids or request id.
  | { event: 'billing_checkout_cancel_requested' }
  | { event: 'billing_sync_completed' }
  | { event: 'billing_sync_timed_out' }
  // Missed-webhook self-heal (POST /billing/reconcile). Privacy-safe: event
  // names only — never the Stripe customer/subscription ids, reconciled state
  // values, or request id.
  | { event: 'billing_reconcile_requested' }
  | { event: 'billing_reconcile_failed' }
  | { event: 'billing_divergence_retry_requested' }
  // In-app plan change (POST /billing/plan-change). Same privacy discipline as
  // the checkout events: `plan`/`interval` as low-cardinality product ordinals
  // plus the outcome `direction` (immediate/scheduled). NEVER attach the
  // Stripe customer/subscription/schedule ids, proration amounts, effective
  // timestamps, or request id.
  | { event: 'billing_plan_change_requested'; plan: string; interval: string }
  | { event: 'billing_plan_change_succeeded'; plan: string; interval: string; direction: string }
  | { event: 'billing_plan_change_failed' }
  // Emitted alongside billing_plan_change_succeeded when the change cleared a
  // portal-scheduled cancellation (change.resumed) — same payload discipline.
  | { event: 'billing_plan_change_resumed'; plan: string; interval: string; direction: string }
  | { event: 'billing_portal_started' }
  | { event: 'billing_portal_redirected' }
  | { event: 'billing_refresh_requested' }
  | { event: 'billing_pricing_details_opened' }
  // APP-007 bulk events. Privacy-safe: event names + safe low-cardinality
  // ordinals only. NEVER attach the filename, file size, row count, job id,
  // email, error row, account/Clerk id, request id, result URL, or storage
  // key. `category` is an allowlisted high-level validation category
  // (unsupported_type/oversize/missing_email_header/…), never a value that
  // could echo input. `status` is an allowlisted terminal status ordinal
  // (completed/failed/quota_exceeded/cancelled). The scrub guard below still
  // rejects any leaked CSV-content hint so a row accidentally attached to any
  // future payload throws.
  | { event: 'bulk_page_loaded' }
  | { event: 'bulk_file_selected' }
  | { event: 'bulk_file_validation_failed_by_category'; category: string }
  | { event: 'bulk_upload_started' }
  | { event: 'bulk_job_created' }
  | { event: 'bulk_job_status_viewed' }
  | { event: 'bulk_job_terminal_state_reached'; status: string }
  | { event: 'bulk_result_download_started' }
  | { event: 'bulk_result_download_completed' }
  | { event: 'bulk_refresh_requested' }
  | { event: 'bulk_template_downloaded' }
  | { event: 'bulk_docs_opened' }
  | { event: 'bulk_upgrade_action_selected' }
  // APP-008 webhook events. Privacy-safe: event names only. NEVER attach the
  // endpoint URL, query string, signing secret, webhook id, delivery id, event
  // payload, response status, account/Clerk id, or request id. The scrub guard
  // below rejects any leaked `whsec_` hint so a signing secret accidentally
  // attached to any future payload throws. The destination URL is treated as
  // potentially sensitive (may carry customer-owned query credentials).
  | { event: 'webhooks_page_loaded' }
  | { event: 'webhook_create_started' }
  | { event: 'webhook_created' }
  | { event: 'webhook_secret_copy_requested' }
  | { event: 'webhook_edit_started' }
  | { event: 'webhook_updated' }
  | { event: 'webhook_secret_rotation_started' }
  | { event: 'webhook_secret_rotated' }
  | { event: 'webhook_disabled' }
  | { event: 'webhook_enabled' }
  | { event: 'webhook_deleted' }
  | { event: 'webhook_delivery_history_opened' }
  | { event: 'webhook_test_started' }
  | { event: 'webhook_test_completed' }
  | { event: 'webhooks_refresh_requested' }
  | { event: 'webhook_docs_opened' }
  | { event: 'webhook_upgrade_action_selected' }
  // APP-009 account events. Privacy-safe: event names only. NEVER attach the
  // account id, Clerk id, email, account name, plan, request id, or raw error.
  // The rename payload is empty on purpose — the new name is account data and is
  // never sent through analytics. The scrub guard below still rejects any leaked
  // secret hint.
  | { event: 'account_page_loaded' }
  | { event: 'account_name_updated' }
  | { event: 'account_sign_out_started' }
  | { event: 'account_billing_opened' }
  | { event: 'account_docs_opened' }
  | { event: 'account_support_opened' }
  // Public playground events (anonymous pre-signup surface). Privacy-safe,
  // mirroring the APP-005 playground discipline: event names + allowlisted
  // high-level failure categories ONLY. NEVER attach the analyzed email, risk
  // score, decision, check outcomes, request id, IP, or remaining-budget
  // count (the budget is per-visitor and not analytics' business).
  | { event: 'public_playground_page_loaded' }
  | { event: 'public_playground_analysis_started' }
  | { event: 'public_playground_analysis_succeeded' }
  | { event: 'public_playground_analysis_failed_by_code'; failure_code: string }
  | { event: 'public_playground_signup_opened' };

export interface AnalyticsSink {
  track(event: AnalyticsEvent): void;
}

/** Forbidden substrings scrubbed from any analytics payload defensively. */
export const SCRUB_HINTS = [
  'eisk_',
  'sk_',
  'pk_',
  'Bearer ',
  'checkout.stripe.com',
  'billing.stripe.com',
  // APP-007: bulk payloads may contain email values; guard against any
  // uploaded-content hint leaking into a future payload. The CSV header token
  // and a recognizable marker-prefix keep the guard conservative.
  'person@example.com',
  'email,name',
  // APP-008: webhook signing secret prefix. A reveal-once `whsec_` value
  // accidentally attached to any future payload throws.
  'whsec_',
];

function scrub(value: unknown): void {
  if (typeof value !== 'string') return;
  for (const hint of SCRUB_HINTS) {
    if (value.includes(hint)) {
      throw new Error(
        `[analytics] refusing to emit event containing a sensitive token hint ("${hint}"). ` +
          'This is a bug — sensitive values must never enter analytics.',
      );
    }
  }
}

const noopSink: AnalyticsSink = {
  track(event) {
    if (import.meta.env.DEV) {
      // Validate defensively that no secret material leaked into ANY string
      // field of the payload (not just the known ones). APP-004 attaches no
      // key-scoped payloads, but this guards future additions.
      try {
        scrubEvent(event);
      } catch {
        // Fall through; the validation above is defensive.
      }
      // Intentionally do not log event contents in production.
    }
  },
};

/**
 * Walk every string value of an event payload and reject secret hints. Exported
 * so the privacy contract (the registry test, APP-009 §34) can assert every
 * SCRUB_HINTS token is rejected for any future payload shape. The default
 * no-op sink calls this in development as a defensive tripwire.
 */
export function scrubEvent(event: AnalyticsEvent): void {
  for (const value of Object.values(event as Record<string, unknown>)) {
    if (typeof value === 'string') {
      scrub(value);
    }
  }
}

let sink: AnalyticsSink = noopSink;

export function setAnalyticsSink(next: AnalyticsSink): void {
  sink = next;
}

export function track(event: AnalyticsEvent): void {
  sink.track(event);
}
